import React from 'react';
import PropTypes from 'prop-types';
import camelCase from 'lodash/camelCase';
import get from 'lodash/get';
import omit from 'lodash/omit';
import format from 'date-fns/format';
import { defineMessages, FormattedMessage, FormattedDate } from 'react-intl';

import { arrangeAppointmentsByDay, getDateFromDateString, DAYS_BLOCKED } from './util';

import Table from '../../components/Table';
import Button from '../../components/Button';
import Icon from '../../components/Icon';
import { parseISODate } from '../VisitsAvailability/utils';

import REPVISITS_LOCATION_TYPE from '../../utils/repvisits-location-types';

import './styles.css';

const messages = defineMessages({
  holiday: {
    id: 'visits-availability.holiday',
    defaultMessage: 'Holiday',
  },
  other: {
    id: 'visits-availability.other',
    defaultMessage: 'Other',
  },
  noSchool: {
    id: 'visits-availability.noSchool',
    defaultMessage: 'No School',
  },
  schoolEvent: {
    id: 'visits-availability.schoolEvent',
    defaultMessage: 'School Event',
  },
  outsideAvailabilityWindow: {
    id: 'visits-availability.outsideAvailabilityWindow',
    defaultMessage: 'Outside Availability Window',
  },
});

export const BlockedDay = (props) => {
  const reason = camelCase(props.day.status.replace('BLOCKED_', ''));
  const { repVisitsNextAppointmentDate } = props;
  return (
    <Table.Cell styleName="blocked-holiday" {...omit(props, 'day', 'repVisitsNextAppointmentDate')}>
      <div styleName="blocked-header"><FormattedMessage id="visits-availability.blocked" defaultMessage="Blocked" /></div>
      <div><FormattedMessage {...messages[reason]} /></div>
      <div>
        {props.colSpan === 5 && repVisitsNextAppointmentDate &&
          (
            <span>
              <FormattedMessage
                id="visits-availability.nextAvailability"
                defaultMessage="Next Available Visit is {nextDate}"
                values={{ nextDate: format(parseISODate(repVisitsNextAppointmentDate.date), 'MMM dd, yyyy') }}
              />
            </span>
          )}
        {props.colSpan === 5 && !repVisitsNextAppointmentDate &&
          (
            <span>
              <FormattedMessage
                id="visits-availability.noFutureAvailability"
                defaultMessage="No Future Availability At This Time"
              />
            </span>
          )}
      </div>
    </Table.Cell>
  );
};
BlockedDay.propTypes = {
  day: PropTypes.shape({
    status: PropTypes.string.isRequired,
  }).isRequired,
  colSpan: PropTypes.number,
  repVisitsNextAppointmentDate: PropTypes.shape({
    date: PropTypes.string,
  }),
};

export const EmptyDay = (props) => {
  const { repVisitsNextAppointmentDate } = props;
  return (
    <Table.Cell styleName="empty" {...omit(props, 'repVisitsNextAppointmentDate')}>
      <FormattedMessage id="visits-availability.noAvailability" defaultMessage="No Appointments Available" />
      {props.colSpan === 5 && repVisitsNextAppointmentDate &&
      (
        <span>
          <br />
          <FormattedMessage
            id="visits-availability.nextAvailability"
            defaultMessage="Next Available Visit is {nextDate}"
            values={{ nextDate: format(parseISODate(repVisitsNextAppointmentDate.date), 'MMM dd, yyyy') }}
          />
        </span>
      )}
      {props.colSpan === 5 && !repVisitsNextAppointmentDate &&
      (
        <span>
          <br />
          <FormattedMessage
            id="visits-availability.noFutureAvailability"
            defaultMessage="No Future Availability At This Time"
          />
        </span>
      )}
    </Table.Cell>
  );
};
EmptyDay.propTypes = {
  colSpan: PropTypes.number,
  repVisitsNextAppointmentDate: PropTypes.shape({
    date: PropTypes.string,
  }),
};

export const getIcon = (visitType) => {
  switch (visitType) {
    case REPVISITS_LOCATION_TYPE.VIRTUAL:
      return <Icon name="tv" />;
    case REPVISITS_LOCATION_TYPE.EITHER:
      return (
        <Icon.Group>
          <Icon name="user" />
          <Icon corner="top right" name="pencil" />
        </Icon.Group>
      );
    default:
    case REPVISITS_LOCATION_TYPE.IN_PERSON:
      return <Icon size="large" name="male" />;
  }
};

const VisitsAvailabilitySingle = ({ week, highSchoolBySchoolId, schoolId, onAppointmentClick }) => {
  const weekAsDates = week.map(getDateFromDateString);
  const availabilitySlots = get(highSchoolBySchoolId, 'repVisitsAvailability', []);
  const repVisitsNextAppointmentDate = get(highSchoolBySchoolId, 'repVisitsNextAppointmentDate', null);
  const appointmentsByDay = arrangeAppointmentsByDay(week, availabilitySlots, highSchoolBySchoolId);
  const webInstructions = get(highSchoolBySchoolId, 'repVisitsAccount.webInstructions');
  const numRows = Math.max(3, Math.max(
    ...appointmentsByDay.map((day) => get(day, 'appointments', []).length),
  ));

  return (
    <Table
      fixed
      basic
      unstackable
      textAlign="center"
      data-cy="revisits-visits-availability-table"
      verticalAlign="middle"
      styleName="visits-availability-single"
    >
      <Table.Header styleName="availability-table-header">
        <Table.Row>
          {weekAsDates.map((date) => (
            <Table.HeaderCell key={date.toString()}>
              <FormattedDate value={date} weekday="short" />
              <div styleName="date"><FormattedDate value={date} month="short" day="numeric" /></div>
            </Table.HeaderCell>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body data-cy="visit-availability-table">
        {
          Array.from(Array(numRows), (_, row) => (
            /* eslint-disable react/no-array-index-key */
            <Table.Row key={row}>
              {
                appointmentsByDay.map((day, col) => {
                  const key = `${col}:${row}`;
                  if (!DAYS_BLOCKED.includes(day.status) && day.status !== 'EMPTY') {
                    // for open/partially open
                    const slot = get(day, 'appointments', [])[row];
                    if (slot) {
                      const visitLocationType = (slot.visitLocationType || 'in_person');
                      return (
                        <React.Fragment key={key}>
                          <Table.Cell key={key}>
                            <Button
                              fluid
                              size="small"
                              data-cy={`availability-button-${visitLocationType.toLowerCase()}`}
                              styleName={`availability-button ${visitLocationType.toLowerCase()}`}
                              onClick={() => onAppointmentClick(schoolId, day.id, slot.id, webInstructions)}
                            >
                              { getIcon(visitLocationType) }
                              { slot.startTime.toLowerCase() }
                            </Button>
                          </Table.Cell>
                        </React.Fragment>
                      );
                    }
                    return <Table.Cell key={key} />;
                  }
                  // only make a cell on the first row because of rowspan
                  if (row === 0) {
                    if (day.status === 'EMPTY') {
                      return (
                        <EmptyDay
                          rowSpan={numRows}
                          colSpan={get(day, 'span', 1)}
                          key={key}
                          repVisitsNextAppointmentDate={repVisitsNextAppointmentDate}
                        />
                      );
                    }
                    return (
                      <BlockedDay
                        day={day}
                        rowSpan={numRows}
                        colSpan={get(day, 'span', 1)}
                        key={key}
                        repVisitsNextAppointmentDate={repVisitsNextAppointmentDate}
                      />
                    );
                  }
                  return null;
                })
              }
            </Table.Row>
          )) /* eslint-disable react/no-array-index-key */
        }
      </Table.Body>
    </Table>
  );
};
VisitsAvailabilitySingle.propTypes = {
  week: PropTypes.arrayOf(PropTypes.string).isRequired,
  highSchoolBySchoolId: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    repVisitsAvailability: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      date: PropTypes.string,
      status: PropTypes.string,
      appointments: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        startTime: PropTypes.string,
        maxColleges: PropTypes.number,
        currentColleges: PropTypes.number,
      })),
    })),
    repVisitsNextAppointmentDate: PropTypes.shape({
      date: PropTypes.string,
    }),
  }),
  schoolId: PropTypes.string.isRequired,
  onAppointmentClick: PropTypes.func.isRequired,
};

export default VisitsAvailabilitySingle;
